import * as Yup from 'yup';
import i18n, { TFunction } from 'i18next';

type IValidatorErrorLocaleDescription = Record<string, any>;

type ValidatorErrorLocaleField = Record<string, string>;

type ValidatorErrorLocaleFunctor = (description: IValidatorErrorLocaleDescription) => string;

type ValidatorErrorLocaleFormatter = (
	translationKey: string,
	t: TFunction,
) => ValidatorErrorLocaleFunctor;

function createLocaleObject(t: TFunction) {
	return {
		array: {
			max: t('validation.array.max'),
			min: t('validation.array.min'),
		},
		date: {
			max: t('validation.date.max'),
			min: t('validation.date.min'),
		},
		mixed: {
			default: t('validation.mixed.default'),
			defined: t('validation.mixed.defined'),
			notOneOf: t('validation.mixed.notOneOf'),
			oneOf: t('validation.mixed.oneOf'),
			required: t('validation.mixed.required'),
		},
		number: {
			MoreThan: t('validation.number.MoreThan'),
			integer: t('validation.number.integer'),
			lessThan: t('validation.number.lessThan'),
			max: t('validation.number.max'),
			min: t('validation.number.min'),
			negative: t('validation.number.negative'),
			notEqual: t('validation.number.notEqual'),
			positive: t('validation.number.positive'),
		},
		object: {
			noUnknown: t('validation.object.noUnknown'),
		},
		string: {
			email: t('validation.string.email'),
			length: t('validation.string.length'),
			lowercase: t('validation.string.lowercase'),
			max: t('validation.string.max'),
			min: t('validation.string.min'),
			uppercase: t('validation.string.uppercase'),
			url: t('validation.string.url'),
		},
	};
}

class ValidatorFactory {
	static applyLocaleToYup() {
		// We will be passing the t function, make sure it's parent to `this` does not
		// get lost
		const t = i18n.t.bind(i18n);
		const localeObject = createLocaleObject(t);

		Yup.setLocale(
			ValidatorFactory.applyFormatterToLocaleObject(
				ValidatorFactory.translationPlaceholderFormatter,
				localeObject,
				t,
			),
		);
	}

	static create() {
		i18n.on('loaded', ValidatorFactory.applyLocaleToYup);
		i18n.on('languageChanged', ValidatorFactory.applyLocaleToYup);
		return {
			...Yup,
			localizedString: () =>
				Yup.array(
					validator.object({
						text: validator.string().min(1).max(256).required(),
					}),
				).min(1),
		};
	}

	static removeLeadingObjectPaths(path: string): string {
		return path.replace(/([a-z]+\[[0-9]+\]\.)/gi, '');
	}

	static translationPlaceholderFormatter(
		translationKey: string,
		t: TFunction,
	): ValidatorErrorLocaleFunctor {
		return (options: IValidatorErrorLocaleDescription) => {
			return Object.keys(options).reduce((result, option) => {
				// If we are replacing the path option, prefix it with common.fields to make it
				// accessible for translation
				if (option === 'path') {
					const value = ValidatorFactory.removeLeadingObjectPaths(options[option]);
					return result.replace(`\${${option}}`, t(`common.fields.${value}`));
				}
				return result.replace(`\${${option}}`, options[option]);
			}, translationKey);
		};
	}

	static applyFormatterToLocaleObject(
		fn: ValidatorErrorLocaleFormatter,
		localeObject: Yup.LocaleObject,
		t: TFunction,
	): Yup.LocaleObject {
		// 1. Loop through the locale object per field (eg. mixed, string, number)
		return Object.keys(localeObject).reduce((updatedObject, fieldKey) => {
			// 2. Take out the object with all rules for this field
			const field = (localeObject as Record<string, ValidatorErrorLocaleField>)[fieldKey];

			return {
				...updatedObject,
				// 3. Loop through all the rules of this field
				[fieldKey]: Object.keys(field).reduce((updatedCategory, ruleKey) => {
					const translationKey: string = field[ruleKey];

					// 4. If the rare scenario happends where a field's error message is null, return null
					// this is type-wise not possible, but can present itself when working with data sent
					// by servers
					if (translationKey == null) {
						return {
							...updatedCategory,
							[ruleKey]: null,
						};
					}

					// 5. Create the `functor` function that can be used by Yup
					// The functor function has this signature: `({ path }) => string`.
					// It is used as a curried function to give the implementing developer freedom to access
					// translation keys as well.
					return {
						...updatedCategory,
						[ruleKey]: fn(translationKey, t),
					};
				}, {}),
			};
		}, {});
	}
}

const validator = (() => {
	return ValidatorFactory.create();
})();

export default validator;
